/**
 * "Compose" on the standalone Email screen has no record already open to
 * anchor to — every other composer entry point (Account/Lead/Contact detail,
 * a Leads-list row) already knows which record it's sending about. This is
 * the step that supplies one: search leads/contacts/accounts, pick a row, and
 * `onPick` hands the caller exactly the `{ anchor, defaultEmail, accountId }`
 * shape `EmailComposerModal` already expects — that component is otherwise
 * untouched (see docs/modules/emailInbox.model.md §1: recipient must resolve
 * to an existing CRM record, so there is deliberately no "type any address"
 * option here).
 */
import { useEffect, useState } from 'react';
import { Modal, Field, ButtonSpinner } from '../../components/common/index.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useLeads } from '../leads/useLeads.js';
import { useContacts } from '../contacts/useContacts.js';
import { useAccounts } from '../accounts/useAccounts.js';

function PickerGroup({ label, icon, children }) {
  return (
    <div className="mb-3">
      <div className="t-2xs ink-4 fw-650 mb-1" style={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>
        <i className={`${icon} me-1`} aria-hidden="true" />{label}
      </div>
      <div className="d-flex flex-column gap-1">{children}</div>
    </div>
  );
}

function PickerRow({ title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-start p-2 px-3"
      style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}
    >
      <div className="t-sm fw-600">{title}</div>
      {subtitle && <div className="t-xs ink-3">{subtitle}</div>}
    </button>
  );
}

export function RecipientPickerModal({ open, onClose, onPick }) {
  const [q, setQ] = useState('');
  const debounced = useDebounce(q);

  useEffect(() => { if (open) setQ(''); }, [open]);

  const searchParams = { q: debounced || undefined, limit: 6 };
  const { data: leadsPage, isFetching: leadsBusy } = useLeads(searchParams);
  const { data: contactsPage, isFetching: contactsBusy } = useContacts(searchParams);
  const { data: accountsPage, isFetching: accountsBusy } = useAccounts(searchParams);

  if (!open) return null;

  const leads = leadsPage?.data ?? [];
  const contacts = contactsPage?.data ?? [];
  const accounts = accountsPage?.data ?? [];
  const busy = leadsBusy || contactsBusy || accountsBusy;
  const nothing = !busy && leads.length === 0 && contacts.length === 0 && accounts.length === 0;

  return (
    <Modal open={open} onClose={onClose} title="Compose email" size="lg">
      <Field label="Who is this to?" help="Every email attaches to a lead, contact or account, so it shows up on that record's history too.">
        <input
          autoFocus type="text" className="form-control" value={q}
          onChange={(e) => setQ(e.target.value)} placeholder="Search by name, company or email…"
        />
      </Field>

      {busy && <div className="t-sm ink-3 mt-2"><ButtonSpinner className="me-2" />Searching…</div>}
      {nothing && <div className="t-sm ink-3 mt-2">No matches.</div>}

      {leads.length > 0 && (
        <PickerGroup label="Leads" icon="bi-magnet-fill">
          {leads.map((l) => (
            <PickerRow
              key={l.id} title={l.full_name} subtitle={l.email}
              onClick={() => onPick({ anchor: { lead_id: l.id }, defaultEmail: l.email ?? '' })}
            />
          ))}
        </PickerGroup>
      )}

      {contacts.length > 0 && (
        <PickerGroup label="Contacts" icon="bi-person-lines-fill">
          {contacts.map((c) => (
            <PickerRow
              key={c.id} title={c.full_name} subtitle={c.email}
              onClick={() => onPick({ anchor: { contact_id: c.id }, defaultEmail: c.email ?? '' })}
            />
          ))}
        </PickerGroup>
      )}

      {accounts.length > 0 && (
        <PickerGroup label="Accounts" icon="bi-building">
          {accounts.map((a) => (
            <PickerRow
              key={a.id} title={a.name} subtitle="Choose a contact at this account next"
              onClick={() => onPick({ anchor: { account_id: a.id }, defaultEmail: '', accountId: a.id })}
            />
          ))}
        </PickerGroup>
      )}
    </Modal>
  );
}
